// Centralized API helper with automatic token refresh on 401
// Usage: import { apiFetch, getAccessToken, logout } from './lib/api';

import { API_BASE } from './config';

function getTokens() {
  try {
    const access = localStorage.getItem('access_token') || null;
    const refresh = localStorage.getItem('refresh_token') || null;
    return { access, refresh };
  } catch (_) {
    return { access: null, refresh: null };
  }
}

function isTokenExpired(token) {
  if (!token) return true;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    const { exp } = JSON.parse(jsonPayload);
    if (!exp) return false;
    // Check if expired (with 10s buffer)
    return (Date.now() / 1000) > (exp - 10);
  } catch (_) {
    return true;
  }
}

export function setTokens({ access, refresh }) {
  try {
    if (access) {
      localStorage.setItem('access_token', access);
      // Notify same-tab listeners
      try {
        window.dispatchEvent(new StorageEvent('storage', { key: 'access_token', newValue: access }));
        window.dispatchEvent(new CustomEvent('auth-change', { detail: { key: 'access_token', newValue: access } }));
      } catch (_) {}
      // Also drop a non-HttpOnly cookie for dev convenience (same as in AuthCallback)
      document.cookie = `access_token=${encodeURIComponent(access)}; path=/`;
    }
    if (refresh) {
      localStorage.setItem('refresh_token', refresh);
      document.cookie = `refresh_token=${encodeURIComponent(refresh)}; path=/`;
    }
  } catch (_) {}
}

export function clearTokens() {
  try {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    document.cookie = 'access_token=; Max-Age=0; path=/';
    document.cookie = 'refresh_token=; Max-Age=0; path=/';
    // Broadcast logout to other tabs
    const logoutTrigger = String(Date.now());
    localStorage.setItem('logout_trigger', logoutTrigger);
    window.dispatchEvent(new CustomEvent('auth-change', { detail: { key: 'logout_trigger', newValue: logoutTrigger } }));
  } catch (_) {}
}

let refreshPromise = null;

async function refreshTokens() {
  if (refreshPromise) return refreshPromise; // de-duplicate concurrent refresh attempts
  const { access, refresh } = getTokens();
  if (!refresh || !access) {
    clearTokens();
    return Promise.reject(new Error('No refresh or access token'));
  }
  const body = { refresh_token: refresh, token: access };
  refreshPromise = fetch(`${API_BASE}/v1/auth/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(body),
  })
    .then(async (res) => {
      if (!res.ok) throw new Error(`Refresh failed: ${res.status}`);
      const data = await res.json().catch(() => ({}));
      // Accept multiple possible response shapes
      const newAccess = data.access_token || data.access || data.token;
      const newRefresh = data.refresh_token || data.refresh || refresh; // keep old refresh if not provided
      if (!newAccess) throw new Error('No access token in refresh response');
      setTokens({ access: newAccess, refresh: newRefresh });
      return { access: newAccess, refresh: newRefresh };
    })
    .catch((err) => {
      clearTokens();
      throw err;
    })
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

function cloneInit(init) {
  const copy = { ...(init || {}) };
  // Clone headers safely
  if (init && init.headers) {
    if (init.headers instanceof Headers) {
      const h = new Headers();
      init.headers.forEach((v, k) => h.set(k, v));
      copy.headers = h;
    } else if (Array.isArray(init.headers)) {
      copy.headers = [...init.headers];
    } else {
      copy.headers = { ...init.headers };
    }
  } else {
    copy.headers = {};
  }
  return copy;
}

export async function apiFetch(input, init = {}, { retryOn401 = true } = {}) {
  const { access } = getTokens();

  // Check if it's the /me endpoint or any other request that might benefit from pre-emptive refresh
  const isMe = typeof input === 'string' && input.includes('/v1/players/me');

  if (access && isTokenExpired(access)) {
    try {
      await refreshTokens();
    } catch (_) {
      // If refresh fails and it was /me, we might want to fail early, but for others we can try original
      if (isMe) {
        clearTokens();
        return new Response(JSON.stringify({ error: 'Session expired' }), { status: 401 });
      }
    }
  }

  const req = cloneInit(init);
  // Inject Authorization header if not present and we have access token
  const headers = req.headers instanceof Headers ? req.headers : new Headers(req.headers);
  const { access: currentAccess } = getTokens();
  if (currentAccess && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${currentAccess}`);
  }
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  req.headers = headers;
  req.credentials = req.credentials || 'include';

  let res = await fetch(input, req);
  if (res.status !== 401 || !retryOn401) return res;

  // Attempt refresh and retry once if we got 401
  try {
    await refreshTokens();
  } catch (_) {
    return res; // give original 401 back; caller may handle logout UI
  }

  const retryReq = cloneInit(init);
  const retryHeaders = retryReq.headers instanceof Headers ? retryReq.headers : new Headers(retryReq.headers || {});
  const { access: newAccess } = getTokens();
  if (newAccess) retryHeaders.set('Authorization', `Bearer ${newAccess}`);
  if (!retryHeaders.has('Accept')) retryHeaders.set('Accept', 'application/json');
  retryReq.headers = retryHeaders;
  retryReq.credentials = retryReq.credentials || 'include';
  return fetch(input, retryReq);
}

export function getAccessToken() {
  return getTokens().access;
}

export function logout() {
  clearTokens();
  try {
    const trigger = localStorage.getItem('logout_trigger');
    window.dispatchEvent(new StorageEvent('storage', { key: 'logout_trigger', newValue: trigger }));
  } catch (_) {}
}

export function getMe() {
    return apiFetch(`${API_BASE}/v1/players/me`, {
        headers: { 'Accept': 'application/json' },
        credentials: 'include'
    });
}

export function getServers(signal) {
    return apiFetch(`${API_BASE}/v1/servers/`, { signal, credentials: 'include' });
}

export function addServers(servers) {
    return apiFetch(`${API_BASE}/v1/servers/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ servers }),
    });
}

export function getDangerZone() {
    return apiFetch(`${API_BASE}/v1/dangerzone/`, { credentials: 'include' });
}
